import moment from "moment";
import {
  formatChange,
  convertTo24HourFormat,
  convertTo12,
  isValidTimeFormat,
  formatTimeTo24Hr,
  isEndTimeGreater,
  convertTo24Hour,
} from "./timeFormat.js";
import { timeFormatChange, formatTime } from "./timeValidation.js";
import { formatDuration } from "./uiHelpers.js";

/**
 * Time Handler Functions
 * Functions for handling time input changes and validations
 */

/**
 * Create time change handlers for 12-hour format
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setStartTimeEdit - Setter for start time
 * @param {Function} setters.setStartTimeTemp - Setter for start time temp
 * @param {Function} setters.setEndTimeEdit - Setter for end time
 * @param {Function} setters.setEndTimeTemp - Setter for end time temp
 * @param {Function} setters.setUStartTimeError - Setter for start time error
 * @param {Function} setters.setUEndTimeError - Setter for end time error
 * @returns {Object} Object containing changeStartTime and changeEndTime functions
 */
export const create12HourTimeHandlers = ({
  setStartTimeEdit,
  setStartTimeTemp,
  setEndTimeEdit,
  setEndTimeTemp,
  setUStartTimeError,
  setUEndTimeError,
}) => {
  const changeStartTime = (startTime) => {
    let formattedTime = timeFormatChange(startTime);
    if (formattedTime === false) {
      setUStartTimeError(true);
      return;
    }
    setStartTimeEdit(formattedTime);
    setStartTimeTemp(formattedTime);
  };

  const changeEndTime = (endTime) => {
    let formattedTime = timeFormatChange(endTime);
    if (formattedTime === false) {
      setUEndTimeError(true);
      return;
    }
    setEndTimeEdit(formattedTime);
    setEndTimeTemp(formattedTime);
  };

  return { changeStartTime, changeEndTime };
};

/**
 * Create time change handlers for 24-hour format
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setStartTimeEdit24 - Setter for start time 24-hour
 * @param {Function} setters.setStartTimeEdit - Setter for start time 12-hour
 * @param {Function} setters.setEndTimeEdit24 - Setter for end time 24-hour
 * @param {Function} setters.setEndTimeEdit - Setter for end time 12-hour
 * @param {Function} setters.setUStartTimeError - Setter for start time error
 * @param {Function} setters.setUEndTimeError - Setter for end time error
 * @param {Function} setters.setUFormatedTimeError - Setter for formatted time error
 * @param {Function} setters.setUDuration - Setter for duration
 * @param {Function} setters.setUFormatedTime - Setter for formatted time
 * @param {Function} setters.getEndTimeEdit24 - Getter for current end time in 24-hour format
 * @param {Function} setters.getStartTimeEdit24 - Getter for current start time in 24-hour format
 * @param {Function} formatTime - Function to format time
 * @returns {Object} Object containing changeStartTime24 and changeEndTime24 functions
 */
export const create24HourTimeHandlers = ({
  setStartTimeEdit24,
  setStartTimeEdit,
  setEndTimeEdit24,
  setEndTimeEdit,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  setUDuration,
  setUFormatedTime,
  getEndTimeEdit24,
  getStartTimeEdit24,
  formatTime,
}) => {
  const changeStartTime24 = (startTime) => {
    setStartTimeEdit24(startTime);
    if (formatChange(startTime)) {
      var convertedTim = convertTo24HourFormat(startTime);
      setStartTimeEdit24(convertedTim);
      setStartTimeEdit(convertTo12(convertedTim));
      setUStartTimeError(false);
    } else {
      setUStartTimeError(true);
    }

    const currentEndTime24 = getEndTimeEdit24();
    if (
      !isValidTimeFormat(formatTimeTo24Hr(startTime)) ||
      !isValidTimeFormat(formatTimeTo24Hr(currentEndTime24))
    ) {
      setUFormatedTimeError(true);
    }

    if (
      isEndTimeGreater(
        formatTimeTo24Hr(startTime),
        formatTimeTo24Hr(currentEndTime24)
      )
    ) {
      const SrtTime = convertTo12(formatTimeTo24Hr(startTime));
      const endTime = convertTo12(formatTimeTo24Hr(currentEndTime24));
      const format = "h:mm A";
      const time1 = moment(SrtTime, format).tz(global.config.getTimeZone());
      const time2 = moment(endTime, format).tz(global.config.getTimeZone());

      const timeDifferenceInSeconds = time2.diff(time1, "seconds");
      if (timeDifferenceInSeconds > 0) {
        setUDuration(parseInt(timeDifferenceInSeconds));
        setUFormatedTime(formatDuration(parseInt(timeDifferenceInSeconds), formatTime));
        setUStartTimeError(false);
        setUEndTimeError(false);
        setUFormatedTimeError(false);
      }
    } else {
      setUStartTimeError(true);
      setUFormatedTimeError(true);
    }
  };

  const changeEndTime24 = (endTime) => {
    setEndTimeEdit24(endTime);
    if (formatChange(endTime)) {
      var convertedTim = convertTo24HourFormat(endTime);
      setEndTimeEdit24(convertedTim);
      setEndTimeEdit(convertTo12(convertedTim));
      setUStartTimeError(false);
    } else {
      setUStartTimeError(true);
    }

    const currentStartTime24 = getStartTimeEdit24();
    if (
      !isValidTimeFormat(currentStartTime24) ||
      !isValidTimeFormat(convertTo24HourFormat(endTime))
    ) {
      setUFormatedTimeError(true);
    }
    if (isEndTimeGreater(currentStartTime24, convertTo24HourFormat(endTime))) {
      const SrtTime = convertTo12(currentStartTime24);
      const edTime = convertTo12(convertTo24HourFormat(endTime));
      const format = "h:mm A";
      const time1 = moment(SrtTime, format).tz(global.config.getTimeZone());
      const time2 = moment(edTime, format).tz(global.config.getTimeZone());
      const timeDifferenceInSeconds = time2.diff(time1, "seconds");
      if (timeDifferenceInSeconds > 0) {
        setUDuration(parseInt(timeDifferenceInSeconds));
        setUFormatedTime(formatDuration(parseInt(timeDifferenceInSeconds), formatTime));
        setUStartTimeError(false);
        setUEndTimeError(false);
        setUFormatedTimeError(false);
      }
    } else {
      setUEndTimeError(true);
      setUFormatedTimeError(true);
    }
  };

  return { changeStartTime24, changeEndTime24 };
};

/**
 * Handle time show edit (duration input)
 * @param {Object} params - Parameters object
 * @param {string} params.timeSec - Time string in HH:mm:ss format
 * @param {Date} params.dateEdit - Date object
 * @param {string} params.startTimeEdit - Start time string
 * @param {Function} params.setUDuration - Setter for duration
 * @param {Function} params.setEndTimeEdit - Setter for end time
 * @param {Function} params.setUFormatedTime - Setter for formatted time
 * @param {Function} params.setUFormatedTimeError - Setter for formatted time error
 * @param {Function} params.setUStartTimeError - Setter for start time error
 * @param {Function} params.setUEndTimeError - Setter for end time error
 * @param {Function} params.convertTimeStamp - Function to convert timestamp
 */
export const handleTimeShowEdit = ({
  timeSec,
  dateEdit,
  startTimeEdit,
  setUDuration,
  setEndTimeEdit,
  setUFormatedTime,
  setUFormatedTimeError,
  setUStartTimeError,
  setUEndTimeError,
  convertTimeStamp,
}) => {
  const { validateTimeFormat } = require("./timeValidation.js");
  
  if (validateTimeFormat(timeSec)) {
    const timeString = timeSec;
    const [hours, minutes, seconds] = timeString.split(":").map(Number);
    const totalSeconds =
      parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds);
    const formattedDates = convertTimeStamp(
      dateEdit,
      startTimeEdit && startTimeEdit.trim()
    );
    const timestampInSeconds = Math.floor(
      new Date(formattedDates).getTime() / 1000
    );
    setUDuration(parseInt(totalSeconds));
    const upcomingTimeInSeconds =
      parseInt(timestampInSeconds) + parseInt(totalSeconds);
    const upcomingTime = new Date(parseInt(upcomingTimeInSeconds) * 1000);
    const formattedTime =
      upcomingTime &&
      upcomingTime
        .toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace("am", "AM")
        .replace("pm", "PM");
    setUFormatedTimeError(false);
    setEndTimeEdit(formattedTime);
    setUFormatedTimeError(false);
    setUStartTimeError(false);
    setUEndTimeError(false);
  } else {
    setUFormatedTimeError(true);
  }
  setUFormatedTime(timeSec);
};
